import React, { useState, useRef, useEffect } from 'react';
import './ClientBriefPanel.css';
import { saveBrief, saveCampaign } from '../firebaseHelpers';
import { getAuth } from 'firebase/auth';
import { collection, onSnapshot, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { getFunctions, httpsCallable } from "firebase/functions";
import { generateBrief } from '../services/apiService';
import { Bot, PenTool } from 'lucide-react';

const initialPrompts = {
  campaignName: '',
  product: '',
  goal: '',
  audience: '',
  message: '',
  brandToneStyle: '',
  requirements: '',
};

const initialScriptLines = [
  { type: 'hook', text: '', notes: '', example: null },
  { type: 'line', text: '', notes: '', example: null },
  { type: 'cta', text: '', notes: '', example: null },
];

const loadingSteps = [
  "Analyzing your brief prompts...",
  "Understanding your brand voice...",
  "Crafting your creative brief...",
  "Adding strategic insights...",
  "Polishing the final touches...",
  "Almost ready!"
];

// Example campaigns for demonstration; in real use, pass as a prop
const exampleCampaigns = ['Spring Promo', 'TikTok Launch'];

export default function ClientBriefPanel({ open, onClose, brief = null, startStep = 1, onBriefSaved, onBriefGenerated, initialCampaign }) {
  // Brief panel component - handles brief creation and editing only
  const [step, setStep] = useState(1); // 1: Creation Method, 2: Campaign & Brief Setup, 3: AI Prompts
  const [mode, setMode] = useState(''); // 'ai' or 'manual' - will be set in step 1
  const [prompts, setPrompts] = useState(initialPrompts);
  const [scriptMode, setScriptMode] = useState('ai'); // 'ai' or 'manual'
  const [scriptLibrary, setScriptLibrary] = useState([]);
  const [activeScriptIdx, setActiveScriptIdx] = useState(0);
  const [selectedCampaign, setSelectedCampaign] = useState(initialCampaign || brief?.campaign || '');
  const [selectedBrief, setSelectedBrief] = useState(brief || null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEnhancingSection, setIsEnhancingSection] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [toast, setToast] = useState('');
  
  // Handle dynamic loading steps
  useEffect(() => {
    let interval;
    if (isLoading) {
      setLoadingStep(0);
      interval = setInterval(() => {
        setLoadingStep(prev => {
          const nextStep = prev + 1;
          // Stop at the last step (Almost ready!)
          if (nextStep >= loadingSteps.length - 1) {
            clearInterval(interval);
            return loadingSteps.length - 1;
          }
          return nextStep;
        });
      }, 1200); // Change step every 1200ms
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLoading]);
  const panelRef = useRef(null);
  const responseTopRef = useRef(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [briefsList, setBriefsList] = useState([]);
  const [selectedBriefId, setSelectedBriefId] = useState('');
  const [isCreatingNewBrief, setIsCreatingNewBrief] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showAutoSaveToast, setShowAutoSaveToast] = useState(false);
  const [showPostSaveOptions, setShowPostSaveOptions] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [extraHooksCount, setExtraHooksCount] = useState(0);
  const [extraCTAsCount, setExtraCTAsCount] = useState(0);
  const [aiPromptComplete, setAiPromptComplete] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const [lastAiResponse, setLastAiResponse] = useState('');
  const [editingSection, setEditingSection] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [bodyLines, setBodyLines] = useState([{ text: '', action: '', notes: '' }]);
  const [scriptCreationMode, setScriptCreationMode] = useState('ai');
  const [isAddingNewScript, setIsAddingNewScript] = useState(false);
  const [newScriptName, setNewScriptName] = useState('');
  const [aiScriptStyle, setAiScriptStyle] = useState('trend');

  // New state for brief settings
  const [briefSettings, setBriefSettings] = useState({
    platform: '',
    creativeControl: 'exact', // 'exact', 'some', 'full'
    videoLength: '30', // in seconds
    videoFormat: '9:16',
    additionalHooks: 0,
    additionalCTAs: 0
  });

  const platforms = [
    { value: 'meta', label: 'Meta (Facebook/Instagram)', icon: '📱' },
    { value: 'tiktok', label: 'TikTok', icon: '��' },
    { value: 'youtube', label: 'YouTube', icon: '▶️' }
  ];

  const creativeControlOptions = [
    { value: 'exact', label: 'Follow script exactly', icon: '📝', description: 'Creators must follow the script word for word' },
    { value: 'some', label: 'Add creative flair', icon: '✨', description: 'Creators can add their style while keeping key points' }
  ];

  const videoLengths = [
    { value: '30', label: '30 seconds', icon: '⏱️' },
    { value: '60', label: '1 minute', icon: '⏲️' },
    { value: '90', label: '1:30', icon: '⏳' }
  ];

  const videoFormats = [
    { value: '9:16', label: '9:16 Vertical', icon: '📱' },
    { value: '16:9', label: '16:9 Landscape', icon: '🖥️' },
    { value: '1:1', label: '1:1 Square', icon: '🔲' }
  ];

  const promptFields = [
    { name: 'goal', label: 'Brief Goal', placeholder: 'What\'s the key outcome you\'d like from this UGC content? (e.g. spark interest, show real user experience, drive clicks, or educate viewers)' },
    { name: 'product', label: 'Product/Service', placeholder: 'Tell us a bit about the product/service and what makes it unique, and why people love it.' },
    { name: 'brandToneStyle', label: 'Brand Tone/Style', placeholder: 'How should this content sound and feel to match your brand? (e.g. calm and trustworthy, fun and upbeat, or confident and bold)' },
    { name: 'audience', label: 'Target Audience', placeholder: 'Who do you want the creator to speak to? Describe your ideal viewer or customer.' },
    { name: 'message', label: 'Key Message', placeholder: 'What should the creator make sure to communicate in the video? (e.g. key benefits, features, or value)' },
    { name: 'requirements', label: 'Special Requirements (optional)', placeholder: 'Anything specific the creator should know? (e.g. show packaging, mention a discount, or avoid certain phrases)' },
  ];

  const [activePromptSection, setActivePromptSection] = useState(promptFields[0].name);

  // Manual brief section headings (same as AI brief)
  const manualBriefSectionsList = [
    { name: 'quickBriefDescription', label: 'QUICK BRIEF DESCRIPTION' },
    { name: 'briefOverview', label: '1. BRIEF OVERVIEW' },
    { name: 'aboutProduct', label: '2. ABOUT OUR PRODUCT' },
    { name: 'brandVoice', label: '3. BRAND VOICE & TONE' },
    { name: 'visualStyle', label: '4. VISUAL STYLE & FEEL' },
    { name: 'contentGuidelines', label: '5. CONTENT GUIDELINES' },
    { name: 'quickTips', label: '6. QUICK TIPS' },
  ];

  const [manualBriefSections, setManualBriefSections] = useState({
    quickBriefDescription: '',
    briefOverview: '',
    aboutProduct: '',
    brandVoice: '',
    visualStyle: '',
    contentGuidelines: '',
    quickTips: '',
  });

  const handleManualBriefChange = (section, value) => {
    console.log('Manual brief change:', section, value.substring(0, 50) + '...');
    setManualBriefSections(prev => ({ ...prev, [section]: value }));
    
    // Auto-save with debounce (save after 2 seconds of no changes)
    clearTimeout(window.manualBriefSaveTimeout);
    window.manualBriefSaveTimeout = setTimeout(() => {
      console.log('Auto-save triggered from manual change');
      autoSaveBrief();
    }, 2000);
  };

  // Fetch campaigns when component mounts
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) return;

    // Set up real-time listener for campaigns
    const campaignsRef = collection(db, 'users', user.uid, 'campaigns');
    const unsubscribe = onSnapshot(campaignsRef, (snapshot) => {
      const campaignsList = [];
      snapshot.forEach((doc) => {
        campaignsList.push({ id: doc.id, ...doc.data() });
      });
      setCampaigns(campaignsList);
    }, (error) => {
      console.error('Error fetching campaigns:', error);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Prefill state if brief or step changes
  React.useEffect(() => {
    if (open && brief) {
      setSelectedCampaign(brief.campaign || '');
      setPrompts(p => ({ ...p, campaignName: brief.title || '' }));
      setScriptLibrary(brief.scripts && brief.scripts.length > 0 ? brief.scripts : [{ id: 1, name: 'Script 1', lines: initialScriptLines }]);
      setActiveScriptIdx(0);
      
      // Check if the brief has AI response content
      if (brief.aiResponse) {
        setAiResponse(brief.aiResponse);
        setAiPromptComplete(true);
        setStep(3); // Go directly to content creation to show the AI response
        setMode('ai');
      } else if (brief.manualBriefSections) {
        setManualBriefSections(brief.manualBriefSections);
        setStep(3); // Go directly to content creation to show the manual brief
        setMode('manual');
      } else {
        setStep(2); // Go to campaign setup for editing
        setMode('ai'); // Default to AI mode for editing
      }
      
      setIsCreatingNewBrief(false);
      setSelectedBriefId(brief.id || '');
    } else if (open && !brief) {
      setSelectedCampaign('');
      setPrompts(initialPrompts);
      setScriptLibrary([]); // Start with empty script library
      setActiveScriptIdx(0);
      setStep(1); // Start with creation method selection
      setMode('');
      setIsCreatingNewBrief(true);
      setSelectedBriefId('');
      // Clear AI response states for new brief
      setAiPromptComplete(false);
      setAiResponse('');
      setLastAiResponse('');
      setEditingSection(null);
      setIsRegenerating(false);
      // Reset manual brief sections for new brief
      setManualBriefSections({
        quickBriefDescription: '',
        briefOverview: '',
        aboutProduct: '',
        brandVoice: '',
        visualStyle: '',
        contentGuidelines: '',
        quickTips: '',
      });
    }
  }, [open, brief, startStep]);

  // Reset scroll when step/mode/open changes
  useEffect(() => {
    if (!open) return;
    const resetScroll = () => {
      if (panelRef.current) {
        panelRef.current.scrollTop = 0;
      }
    };
    const raf = requestAnimationFrame(resetScroll);
    const timeout = setTimeout(resetScroll, 60);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
    };
  }, [open, step, mode]);

  // Ensure generated response starts at the top
  useEffect(() => {
    if (!open || step !== 3) return;

    const scrollToTop = () => {
      if (panelRef.current) {
        panelRef.current.scrollTop = 0;
      }
      if (aiPromptComplete || aiResponse) {
        responseTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    const raf = requestAnimationFrame(scrollToTop);
    const timeout = setTimeout(scrollToTop, 100);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
    };
  }, [open, step, aiPromptComplete, aiResponse]);

  // Fetch briefs for selected campaign
  useEffect(() => {
    if (!selectedCampaign) {
      setBriefsList([]);
      return;
    }
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;
    const briefsRef = collection(db, 'users', user.uid, 'campaigns', selectedCampaign, 'briefs');
    const unsubscribe = onSnapshot(briefsRef, (snapshot) => {
      const fetchedBriefs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBriefsList(fetchedBriefs);
    });
    return () => unsubscribe();
  }, [selectedCampaign, open]);

  // Updated useEffect for loading brief data
  useEffect(() => {
    if (!selectedBriefId || isCreatingNewBrief) return;
    
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;
    
    const briefRef = doc(db, 'users', user.uid, 'campaigns', selectedCampaign, 'briefs', selectedBriefId);
    getDoc(briefRef).then((docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Ensure all fields have string values, not undefined
        const safeData = {
          campaignName: data.campaignName || data.title || '',
          product: data.product || '',
          goal: data.goal || '',
          audience: data.audience || '',
          message: data.message || '',
          brandToneStyle: data.brandToneStyle || data.tone || data.videoStyle || '',
          requirements: data.requirements || '',
        };
        setPrompts(safeData);
        setScriptLibrary(data.scripts || [{ id: 1, name: 'Script 1', lines: initialScriptLines }]);
        // If the brief has an AI response, show the preview
        if (data.aiResponse) {
          setAiResponse(data.aiResponse);
          setAiPromptComplete(true);
        } else {
          setAiResponse('');
          setAiPromptComplete(false);
        }
        // If the brief has manual sections, load them
        if (data.manualBriefSections) {
          setManualBriefSections(data.manualBriefSections);
        }
      }
    }).catch(error => {
      console.error('Error loading brief:', error);
      setError('Failed to load brief data');
    });
  }, [selectedBriefId, isCreatingNewBrief, selectedCampaign]);

  // Add function to count extra hooks and CTAs
  const updateExtraCounts = (lines) => {
    let hooks = 0;
    let ctas = 0;
    
    lines.forEach((line, idx) => {
      if (line.type === 'hook' && idx !== 0) {
        hooks++;
      } else if (line.type === 'cta' && idx !== lines.length - 1) {
        ctas++;
      }
    });
    
    setExtraHooksCount(hooks);
    setExtraCTAsCount(ctas);
  };

  // Update useEffect to initialize counts when script library changes
  useEffect(() => {
    if (scriptLibrary[activeScriptIdx]?.lines) {
      updateExtraCounts(scriptLibrary[activeScriptIdx].lines);
    }
  }, [scriptLibrary, activeScriptIdx]);

  // Updated handlePromptChange to ensure values are always strings
  const handlePromptChange = (e) => {
    const { name, value } = e.target;
    setPrompts(prevPrompts => ({ 
      ...prevPrompts, 
      [name]: value || '' // Ensure value is never undefined
    }));
  };

  const handleCampaignChange = (e) => {
    setSelectedCampaign(e.target.value);
  };

  // Script Library Handlers
  const handleScriptLineChange = (idx, field, value) => {
    setScriptLibrary(scripts => scripts.map((script, sIdx) =>
      sIdx === activeScriptIdx
        ? { ...script, lines: script.lines.map((line, i) => i === idx ? { ...line, [field]: value } : line) }
        : script
    ));
  };

  const handleAddScriptLine = () => {
    setScriptLibrary(scripts => scripts.map((script, sIdx) =>
      sIdx === activeScriptIdx
        ? {
            ...script,
            lines: [
              ...script.lines.slice(0, script.lines.length - 1),
              { type: 'line', text: '', notes: '', example: null },
              script.lines[script.lines.length - 1],
            ]
          }
        : script
    ));
  };

  const handleRemoveScriptLine = (idx) => {
    setScriptLibrary(scripts => scripts.map((script, sIdx) => {
      if (sIdx === activeScriptIdx) {
        const newLines = script.lines.filter((_, i) => i !== idx);
        return { ...script, lines: newLines };
      }
      return script;
    }));
  };

  const handleExampleUpload = (idx, file) => {
    setScriptLibrary(scripts => scripts.map((script, sIdx) =>
      sIdx === activeScriptIdx
        ? { ...script, lines: script.lines.map((line, i) => i === idx ? { ...line, example: file } : line) }
        : script
    ));
  };

  const handleAddScript = () => {
    // Reset brief settings and script creation mode for new script
    setBriefSettings({
      platform: '',
      creativeControl: 'exact',
      videoLength: '30',
      videoFormat: '9:16',
      additionalHooks: 0,
      additionalCTAs: 0
    });
    setScriptCreationMode('ai');
    setBodyLines([{ text: '', action: '', notes: '' }]);
    setIsAddingNewScript(true);
    setNewScriptName('');
    setStep(2);
  };

  const handleScriptNameChange = (value) => {
    setScriptLibrary(scripts => scripts.map((script, sIdx) =>
      sIdx === activeScriptIdx ? { ...script, name: value } : script
    ));
  };

  const handleRemoveScript = (idx) => {
    if (scriptLibrary.length === 1) return;
    setScriptLibrary(scripts => scripts.filter((_, i) => i !== idx));
    setActiveScriptIdx(idx === 0 ? 0 : idx - 1);
  };

  // Handler for selecting a brief
  const handleBriefSelect = (e) => {
    if (e.target.value === '') {
      setSelectedBriefId('');
      setIsCreatingNewBrief(true);
      setPrompts({ ...initialPrompts, campaignName: '' });
      setScriptLibrary([]); // Start with empty script library
      // Reset manual brief sections for new brief
      setManualBriefSections({
        quickBriefDescription: '',
        briefOverview: '',
        aboutProduct: '',
        brandVoice: '',
        visualStyle: '',
        contentGuidelines: '',
        quickTips: '',
      });
    } else {
      setSelectedBriefId(e.target.value);
      setIsCreatingNewBrief(false);
    }
  };

  // Handler for creating a new brief
  const handleCreateNewBrief = () => {
    setSelectedBriefId('');
    setIsCreatingNewBrief(true);
    setPrompts({ ...initialPrompts, campaignName: '' });
    setScriptLibrary([]); // Start with empty script library
    // Reset manual brief sections for new brief
    setManualBriefSections({
      quickBriefDescription: '',
      briefOverview: '',
      aboutProduct: '',
      brandVoice: '',
      visualStyle: '',
      contentGuidelines: '',
      quickTips: '',
    });
  };

  // Handler for saving brief
  const handleSaveBrief = async () => {
    setError('');
    if (saving) return;
    setSaving(true);
    const auth = getAuth();
    const user = auth.currentUser;
    const uid = user?.uid;
    if (!uid) {
      setError('You must be logged in to save.');
      setSaving(false);
      return;
    }
    if (!selectedCampaign) {
      setError('Please select a campaign.');
      setSaving(false);
      return;
    }
    if (!prompts.campaignName || prompts.campaignName.trim() === '') {
      setError('Please enter a brief name.');
      setSaving(false);
      return;
    }
    const campaignId = selectedCampaign;
    const briefId = isCreatingNewBrief ? prompts.campaignName : selectedBriefId;
    // Ensure aiScriptStyle is always included in briefSettings
    const briefSettingsWithStyle = { ...briefSettings, aiScriptStyle };
    const briefData = {
      ...prompts,
      title: prompts.campaignName,
      scripts: scriptLibrary || [{ id: 1, name: 'Script 1', lines: initialScriptLines }],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      briefSettings: briefSettingsWithStyle, // Always include aiScriptStyle
      // Include AI response if it exists and is complete
      ...(aiPromptComplete && aiResponse && { aiResponse }),
      // Include manual brief sections if they exist
      ...(Object.values(manualBriefSections).some(section => section.trim() !== '') && { manualBriefSections }),
      // Include the mode (ai or manual)
      mode: mode,
      // Include campaign ID
      campaignId: campaignId
    };
    try {
      await saveBrief(uid, campaignId, briefId, briefData);
      if (onBriefSaved) onBriefSaved({ ...briefData, id: briefId, campaign: campaignId, campaignId: campaignId });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
      setShowPostSaveOptions(false);
    } catch (err) {
      console.error('Error saving brief:', err);
      setError('Failed to save brief: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  // Add a function to check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (!selectedCampaign) return true;
    if (!prompts.campaignName || prompts.campaignName.trim() === '') return true;
    return false;
  };

  const handleSaveScriptsToBrief = async () => {
    setError('');
    setSaving(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      const uid = user?.uid;
      if (!uid) throw new Error('You must be logged in to save.');
      if (!selectedCampaign) throw new Error('Please select a campaign.');
      if (!prompts.campaignName || prompts.campaignName.trim() === '') throw new Error('Please enter a brief name.');

      const campaignId = selectedCampaign;
      const briefId = isCreatingNewBrief ? prompts.campaignName : selectedBriefId;
      const briefRef = doc(db, 'users', uid, 'campaigns', campaignId, 'briefs', briefId);

      // Update Firestore with scripts and any edited AI response
      const updateData = {
        scripts: scriptLibrary,
        updatedAt: new Date().toISOString()
      };
      
      // Include AI response if it exists and is complete
      if (aiPromptComplete && aiResponse) {
        updateData.aiResponse = aiResponse;
      }
      
      // Include manual brief sections if they exist
      if (Object.values(manualBriefSections).some(section => section.trim() !== '')) {
        updateData.manualBriefSections = manualBriefSections;
      }
      
      await updateDoc(briefRef, updateData);

      // Update local state to reflect changes immediately
      setSelectedBrief(prev => ({
        ...prev,
        scripts: scriptLibrary,
        updatedAt: new Date().toISOString(),
        ...(aiPromptComplete && aiResponse && { aiResponse }),
        ...(Object.values(manualBriefSections).some(section => section.trim() !== '') && { manualBriefSections })
      }));

      // If there's a callback for brief updates, call it
      if (onBriefSaved) {
        onBriefSaved({
          id: briefId,
          campaign: campaignId,
          scripts: scriptLibrary,
          updatedAt: new Date().toISOString(),
          ...(aiPromptComplete && aiResponse && { aiResponse }),
          ...(Object.values(manualBriefSections).some(section => section.trim() !== '') && { manualBriefSections })
        });
      }

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
      if (typeof onClose === 'function') {
        onClose();
      }
    } catch (err) {
      console.error('Error saving scripts:', err);
      setError('Failed to save scripts: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  // Update handleAddHook
  const handleAddHook = () => {
    setScriptLibrary(scripts => scripts.map((script, sIdx) =>
      sIdx === activeScriptIdx
        ? {
            ...script,
            lines: [
              { type: 'hook', text: '', notes: '', example: null },
              ...script.lines
            ]
          }
        : script
    ));
  };

  // Update handleAddCTA
  const handleAddCTA = () => {
    setScriptLibrary(scripts => scripts.map((script, sIdx) =>
      sIdx === activeScriptIdx
        ? {
            ...script,
            lines: [
              ...script.lines,
              { type: 'cta', text: '', notes: '', example: null }
            ]
          }
        : script
    ));
  };

  // When the panel opens or initialCampaign changes, preselect the campaign
  useEffect(() => {
    if (open && initialCampaign) {
      setSelectedCampaign(initialCampaign);
    }
  }, [open, initialCampaign]);

  // Function to parse AI response into sections
  const parseResponseIntoSections = (response) => {
    const sections = {};
    const lines = response.split('\n');
    let currentSection = '';
    let currentContent = [];

    lines.forEach(line => {
      if (line.startsWith('## ')) {
        if (currentSection) {
          sections[currentSection] = currentContent.join('\n');
        }
        currentSection = line.replace('## ', '').trim();
        currentContent = [];
      } else {
        currentContent.push(line);
      }
    });

    if (currentSection) {
      sections[currentSection] = currentContent.join('\n');
    }

    return sections;
  };

  // Helper to update a section in the AI response
  const updateSectionInResponse = (sectionName, newContent) => {
    const sectionHeader = `## ${sectionName}`;
    const lines = aiResponse.split('\n');
    let newLines = [];
    let inSection = false;
    let foundSection = false;

    for (let i = 0; i < lines.length; i++) {
      if (lines[i].trim() === sectionHeader) {
        foundSection = true;
        inSection = true;
        newLines.push(sectionHeader);
        // Add the new content as lines (preserve all whitespace)
        newLines.push(...newContent.split('\n'));
        // Skip old section content
        i++;
        while (i < lines.length && !lines[i].startsWith('## ')) {
          i++;
        }
        i--; // step back so the for loop doesn't skip a section header
        inSection = false;
      } else if (!inSection) {
        newLines.push(lines[i]);
      }
    }

    // If section not found, append it
    if (!foundSection) {
      if (newLines.length > 0 && newLines[newLines.length - 1] !== '') {
        newLines.push('');
      }
      newLines.push(sectionHeader);
      newLines.push(...newContent.split('\n'));
    }

    return newLines.join('\n');
  };

  // Function to regenerate a specific section (uses latest edits)
  const handleRegenerateSection = async (sectionName) => {
    try {
      setIsRegenerating(true);
      setEditingSection(sectionName);

      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('You must be logged in to regenerate sections');
      }

      // Always use the latest edited brief
      const sectionPrompt = `
Please regenerate only the "${sectionName}" section of this UGC creative brief. Keep it concise and conversational.

CAMPAIGN DETAILS:
- Product/Service: ${prompts.product || 'Not specified'}
- Campaign Goal: ${prompts.goal || 'Not specified'}
- Target Audience: ${prompts.audience || 'Not specified'}
- Key Message: ${prompts.message || 'Not specified'}
- Brand Tone/Style: ${prompts.brandToneStyle || 'Not specified'}
- Special Requirements: ${prompts.requirements || 'None'}

Current brief:
${aiResponse}

Please only regenerate the "${sectionName}" section while maintaining the same style and tone.
      `.trim();

      // Use the API service instead of direct Firebase function call
      const data = await generateBrief({
        userprompt: sectionPrompt,
        userId: user.uid,
        campaignId: selectedCampaign,
        briefName: prompts.campaignName
      });
      const sections = parseResponseIntoSections(data.result);
      
      if (sections[sectionName]) {
        // Update only the specific section in the response
        const updatedResponse = updateSectionInResponse(sectionName, sections[sectionName]);
        setAiResponse(updatedResponse);
        
        // Auto-save the updated brief
        console.log('Auto-save triggered from section regeneration');
        await autoSaveBrief(updatedResponse);
      }
    } catch (error) {
      console.error('Error regenerating section:', error);
      setError(`Failed to regenerate section: ${error.message}`);
    } finally {
      setIsRegenerating(false);
      setEditingSection(null);
    }
  };

  // Auto-save helper function
  const autoSaveBrief = async (updatedAiResponse = null) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      console.log('Auto-save attempt:', { user: !!user, selectedCampaign, campaignName: prompts.campaignName });
      
      if (!user || !selectedCampaign || !prompts.campaignName) {
        console.log('Auto-save skipped - missing required data');
        return;
      }

      const briefRef = doc(db, 'users', user.uid, 'campaigns', selectedCampaign, 'briefs', prompts.campaignName);
      
      const updateData = {
        updatedAt: new Date().toISOString(),
        scripts: scriptLibrary || [{ id: 1, name: 'Script 1', lines: initialScriptLines }]
      };

      // Include AI response if provided
      if (updatedAiResponse) {
        updateData.aiResponse = updatedAiResponse;
      } else if (aiResponse) {
        updateData.aiResponse = aiResponse;
      }

      // Include manual brief sections if they exist
      if (Object.values(manualBriefSections).some(section => section.trim() !== '')) {
        updateData.manualBriefSections = manualBriefSections;
      }

      await updateDoc(briefRef, updateData);
      console.log('Brief auto-saved after change');
      
      // Show auto-save toast
      setShowAutoSaveToast(true);
      setTimeout(() => setShowAutoSaveToast(false), 2000);
    } catch (error) {
      console.error('Error auto-saving brief:', error);
    }
  };

  // Update handleGenerateBrief to set the AI response
  const handleGenerateBrief = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Validate required fields
      const requiredFields = ['product', 'goal', 'audience', 'message'];
      const missingFields = requiredFields.filter(field => !prompts[field] || prompts[field].trim() === '');
      
      if (missingFields.length > 0) {
        setError(`Please fill in the following fields: ${missingFields.join(', ')}`);
        setIsLoading(false);
        return;
      }

      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setError('You must be logged in to generate a brief');
        setIsLoading(false);
        return;
      }

      // Create a focused UGC creative brief prompt
      const userPrompt = `
Create a concise, one-page UGC creative brief written directly for creators. It should sound clear, approachable, and professional — like a brand explaining what they need in a friendly, collaborative way.

CAMPAIGN DETAILS:
- Product/Service: ${prompts.product || 'Not specified'}
- Campaign Goal: ${prompts.goal || 'Not specified'}
- Target Audience: ${prompts.audience || 'Not specified'}
- Key Message: ${prompts.message || 'Not specified'}
- Brand Tone/Style: ${prompts.brandToneStyle || 'Not specified'}
- Special Requirements: ${prompts.requirements || 'None'}

Structure the brief with these sections (keep each section to 2–3 bullet points maximum, written in a clear and conversational tone):

## QUICK BRIEF DESCRIPTION
Write a single, compelling line that clearly explains what this UGC content is about and what's being promoted. This should be a standalone description that can be used as a brief summary.

## 1. BRIEF OVERVIEW
- What we're hoping to achieve with this content
- Who we want to reach and why it matters

## 2. ABOUT OUR PRODUCT
- What makes our product or service special
- How we'd love to see it showcased

## 3. BRAND VOICE & TONE
- How we communicate with our audience
- The personality we'd like reflected in the content

## 4. VISUAL STYLE & FEEL
- The kind of visuals, setting, or vibe we'd love to see
- The overall mood or energy we want to capture

## 5. CONTENT GUIDELINES
- How we'd like the content to feel and flow
- Tips for keeping it authentic to your own style

## 6. QUICK TIPS
- A few things that usually work well
- A couple of things to avoid so it feels natural

Keep the entire brief to one page maximum. Write it as if the brand is clearly explaining their needs to a creator — friendly, straightforward, and focused on helping them deliver the best result.
    `.trim();

      // Use the API service to generate the brief
      const data = await generateBrief({
        userprompt: userPrompt,
        userId: user.uid,
        campaignId: selectedCampaign,
        briefName: prompts.campaignName
      });

      // Set the AI response and show the preview
      if (data && (data.result || typeof data === 'string')) {
        const result = typeof data === 'string' ? data : data.result;
        setAiResponse(result);
        setAiPromptComplete(true);
      } else {
        console.error('Invalid response format:', data);
        throw new Error('Invalid response format');
      }

      // Save the prompts and response to Firestore
      const briefRef = doc(db, 'users', user.uid, 'campaigns', selectedCampaign, 'briefs', prompts.campaignName);
      
      // Create a document with prompts and AI response
      const briefData = {
        ...prompts,
        title: prompts.campaignName,
        aiResponse: data.result,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        campaign: selectedCampaign, // This is now the campaign ID
        campaignId: selectedCampaign, // Add explicit campaignId field
        scripts: scriptLibrary || [{ id: 1, name: 'Script 1', lines: initialScriptLines }]
      };

      // Auto-save to Firestore
      await setDoc(briefRef, briefData, { merge: true });
      console.log('Brief auto-saved successfully');
      
      // Show auto-save success message
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

      // Notify parent component that a brief was generated (for immediate UI update)
      if (onBriefGenerated) {
        onBriefGenerated({
          id: prompts.campaignName,
          campaign: selectedCampaign, // This is now the campaign ID
          campaignId: selectedCampaign, // Add explicit campaignId field
          ...briefData
        });
      }

      // Note: onBriefSaved callback is only called when user explicitly saves the brief
      // This allows the user to see the generated content before deciding to save

    } catch (error) {
      console.error('Error generating UGC brief:', error);
      setError(`Failed to generate brief: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter out fluff/closing lines from AI response
  const filterFluffLines = (content) => {
    const fluffPatterns = [
      /looking forward to/i,
      /can'?t wait to/i,
      /thank you/i,
      /excited to see/i,
      /happy creating/i,
      /let us know/i,
      /enjoy creating/i,
      /good luck/i,
      /have fun/i,
    ];
    return content
      .split('\n')
      .filter(line => !fluffPatterns.some(pattern => pattern.test(line)))
      .join('\n');
  };

  // Component to render the AI response
  const renderAIResponse = () => {
    if (!aiResponse) {
      return null;
    }
    const sections = parseResponseIntoSections(aiResponse);
    return (
      <div className="ai-response-container">
        <div className="response-header">
          <img src="/ugc3-logo.png" alt="UGC3" className="brief-logo" />
          <h3>Your Generated Brief</h3>
          <p>Review and edit each section below. You can regenerate individual sections or save the complete brief.</p>
        </div>
        
        <div className="response-sections">
          {Object.entries(sections).map(([sectionName, content], index) => (
            <div key={`${sectionName}-${index}`} className="response-section">
              <div className="section-header">
                <div className="section-title">
                  <span className="section-number">{index + 1}</span>
                  <h4>{sectionName}</h4>
                </div>
                <button
                  className="brief-regenerate-btn"
                  onClick={() => handleRegenerateSection(sectionName)}
                  disabled={isRegenerating && editingSection === sectionName}
                >
                  {isRegenerating && editingSection === sectionName ? (
                    <>
                      <span className="loading-spinner small"></span>
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <span className="regenerate-icon">🔄</span>
                      Regenerate
                    </>
                  )}
                </button>
              </div>
              <textarea
                value={filterFluffLines(content)}
                onChange={(e) => {
                  const updatedResponse = updateSectionInResponse(sectionName, e.target.value);
                  setAiResponse(updatedResponse);
                }}
                className="section-content"
                rows={6}
                placeholder="Edit this section..."
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Component to render the manual brief sections
  const renderManualBriefSections = () => {
    return (
      <div className="ai-response-container">
        <div className="response-header">
          <h3>Your Manual Brief</h3>
          <p>Fill out each section below to create your custom brief. You can enhance individual sections with AI if needed.</p>
        </div>
        
        <div className="response-sections">
          {manualBriefSectionsList.map((section, index) => (
            <div key={section.name} className="response-section">
              <div className="section-header">
                <div className="section-title">
                  <span className="section-number">{index + 1}</span>
                  <h4>{section.label}</h4>
                </div>
                <button
                  className="brief-regenerate-btn"
                  onClick={() => handleEnhanceManualSection(section.name)}
                  disabled={isEnhancingSection}
                >
                  {isEnhancingSection ? (
                    <>
                      <span className="loading-spinner small"></span>
                      Enhancing...
                    </>
                  ) : (
                    <>
                      <span className="regenerate-icon">🚀</span>
                      AI Enhance
                    </>
                  )}
                </button>
              </div>
              <textarea
                value={manualBriefSections[section.name]}
                onChange={(e) => handleManualBriefChange(section.name, e.target.value)}
                className="section-content"
                rows={6}
                placeholder={
                  section.name === 'briefOverview' ? 'What are we trying to achieve with this brief?' :
                  section.name === 'aboutProduct' ? 'What makes our product special and how should it be showcased?' :
                  section.name === 'brandVoice' ? 'How do we talk to our audience and what key points should we emphasize?' :
                  section.name === 'creativeVision' ? 'What look and feel are we going for? What mood should the content create?' :
                  section.name === 'contentGuidelines' ? 'How should the content feel? How can creators keep it authentic to their style?' :
                  'What tips would help creators make this perfect? What should they avoid?'
                }
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // When going back to prompts, save the current AI response
  const handleBackToPrompts = () => {
    setLastAiResponse(aiResponse);
    setAiPromptComplete(false);
  };

  // Restore the previous AI response
  const handleRestorePreviousBrief = () => {
    if (lastAiResponse) {
      setAiResponse(lastAiResponse);
      setAiPromptComplete(true);
    }
  };

  // Enhance a manual brief section with AI
  const handleEnhanceManualSection = async (sectionName) => {
    try {
      setIsEnhancingSection(true);
      setError('');
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setError('You must be logged in to enhance a section');
        setIsEnhancingSection(false);
        return;
      }
      const sectionLabel = manualBriefSectionsList.find(s => s.name === sectionName)?.label || sectionName;
      const enhancePrompt = `Please enhance the following section of a UGC creative brief, keeping the user's style and intent, but making it more engaging and professional.\nSection: ${sectionLabel}\nContent: ${manualBriefSections[sectionName]}`;
      // Use the API service instead of direct Firebase function call
      const data = await generateBrief({
        userprompt: enhancePrompt,
        userId: user.uid,
        campaignId: selectedCampaign,
        briefName: prompts.campaignName
      });
      // Update only the enhanced section
      let enhanced = data.result;
      
      // Remove section labels and clean up the response
      const sectionLabelRegex = new RegExp(`^\\s*Section:\\s*${sectionLabel}\\s*\\n?`, 'i');
      const headingRegex = new RegExp(`^\\s*${sectionLabel}\\s*\\n?`, 'i');
      
      enhanced = enhanced.replace(sectionLabelRegex, '');
      enhanced = enhanced.replace(headingRegex, '');
      enhanced = enhanced.trim();
      
      setManualBriefSections(prev => ({ ...prev, [sectionName]: enhanced }));
      
      // Auto-save the updated brief
      await autoSaveBrief();
    } catch (error) {
      console.error('Error enhancing section:', error);
      setError(`Failed to enhance section: ${error.message}`);
    } finally {
      setIsEnhancingSection(false);
    }
  };

  const handleSaveManualBrief = async () => {
    setError('');
    setSaving(true);
    const auth = getAuth();
    const user = auth.currentUser;
    const uid = user?.uid;
    if (!uid) {
      setError('You must be logged in to save.');
      setSaving(false);
      return;
    }
    if (!selectedCampaign) {
      setError('Please select a campaign.');
      setSaving(false);
      return;
    }
    if (!prompts.campaignName || prompts.campaignName.trim() === '') {
      setError('Please enter a brief name.');
      setSaving(false);
      return;
    }
    const campaignId = selectedCampaign;
    const briefId = isCreatingNewBrief ? prompts.campaignName : selectedBriefId;
    const briefRef = doc(db, 'users', uid, 'campaigns', campaignId, 'briefs', briefId);
    const briefData = {
      type: 'manual',
      manualBriefSections,
      title: prompts.campaignName,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      campaign: selectedCampaign,
      campaignId: selectedCampaign, // Add explicit campaignId field
    };
    try {
      await setDoc(briefRef, briefData, { merge: false });
      if (onBriefSaved) onBriefSaved({ ...briefData, id: briefId, campaign: campaignId, campaignId: campaignId });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
      setStep(2); // Optionally go to script library step
      setShowPostSaveOptions(false);
    } catch (err) {
      console.error('Error saving manual brief:', err);
      setError('Failed to save manual brief: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  function formatSeconds(seconds) {
    seconds = parseInt(seconds, 10);
    if (seconds < 60) return `${seconds}s`;
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  }

  function getVideoLengthPrompt(seconds) {
    seconds = parseInt(seconds, 10);
    if (seconds <= 35) {
      return 'Great for capturing attention. Ideal for platforms like TikTok, Insta Reels, and YouTube Shorts, and keeping viewers engaged.';
    } else if (seconds <= 60) {
      return 'Good for providing more context. Suitable for Instagram and Facebook ads.';
    } else {
      return 'Ideal for longer-form content like YouTube, explainers, and in-depth storytelling.';
    }
  }

  async function handleCreateScriptWithAI() {
    setIsLoading(true);
    setError('');
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('You must be logged in to generate a script');
      }

      const functions = getFunctions();
      const generateScript = httpsCallable(functions, "generate_script");
      const result = await generateScript({
        userId: user.uid,
        campaignId: selectedCampaign,
        scriptName: newScriptName || `Script ${scriptLibrary.length + 1}`,
        scriptType: aiScriptStyle,
        videoLength: briefSettings.videoLength,
        platform: briefSettings.platform,
        creativeControl: briefSettings.creativeControl,
        additionalHooks: briefSettings.additionalHooks,
        additionalCTAs: briefSettings.additionalCTAs,
        aiScriptStyle: aiScriptStyle,
        additionalInstructions: '',
        briefId: isCreatingNewBrief ? prompts.campaignName : selectedBriefId
      });

      if (result.data && result.data.result) {
        // Add the generated script to the script library
        const newScript = {
          id: Date.now(),
          name: newScriptName || `Script ${scriptLibrary.length + 1}`,
          lines: parseScriptContent(result.data.result)
        };
        setScriptLibrary(prev => [...prev, newScript]);
        setActiveScriptIdx(scriptLibrary.length);
        setStep(3); // Move to script editor
      } else {
        setError(result.data?.error || 'No script generated');
      }
    } catch (err) {
      setError('Failed to generate script: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  }

  // Helper function to parse the AI-generated script content into script lines
  function parseScriptContent(content) {
    const lines = [];
    const sectionRegex = /^\d+\.\s*([A-Z ]+)/gm;
    let match;
    let lastIndex = 0;
    let sectionCount = 0;

    while ((match = sectionRegex.exec(content)) !== null) {
      if (sectionCount > 0) {
        // Parse previous section
        const sectionText = content.slice(lastIndex, match.index).trim();
        const fields = {};
        sectionText.split('\n').forEach(line => {
          const fieldMatch = line.match(/^-\s*(Script|Action|Notes):\s*(.*)$/i);
          if (fieldMatch) {
            fields[fieldMatch[1].toLowerCase()] = fieldMatch[2];
          }
        });
        if (Object.keys(fields).length > 0) {
          lines.push(fields);
        }
      }
      lastIndex = match.index + match[0].length;
      sectionCount++;
    }
    // Parse last section
    const sectionText = content.slice(lastIndex).trim();
    const fields = {};
    sectionText.split('\n').forEach(line => {
      const fieldMatch = line.match(/^-\s*(Script|Action|Notes):\s*(.*)$/i);
      if (fieldMatch) {
        fields[fieldMatch[1].toLowerCase()] = fieldMatch[2];
      }
    });
    if (Object.keys(fields).length > 0) {
      lines.push(fields);
    }

    // Assign types based on order (first = hook, last = cta, rest = body lines)
    return lines.map((fields, idx) => {
      if (idx === 0) return { type: 'hook', ...fields };
      if (idx === lines.length - 1) return { type: 'cta', ...fields };
      return { type: 'line', ...fields };
    });
  }

  // Handler to regenerate a single hook (frontend logic; backend endpoint to be implemented)
  async function handleRegenerateHook(hookIdx) {
    setIsLoading(true);
    setError('');
    try {
      // TODO: Replace with actual backend call to regenerate a single hook
      // For now, just simulate with a placeholder
      // const newHook = await regenerateHookFromBackend(hookIdx, ...context);
      const newHook = {
        type: 'hook',
        script: `Regenerated Hook ${hookIdx + 1} (placeholder)`,
        score: '10/10'
      };
      setScriptLibrary(prev => prev.map((script, sIdx) =>
        sIdx === activeScriptIdx
          ? {
              ...script,
              lines: script.lines.map((line, idx) =>
                line.type === 'hook' && idx === hookIdx ? newHook : line
              )
            }
          : script
      ));
    } catch (err) {
      setError('Failed to regenerate hook: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  }

  if (!open) return null;

  return (
    <div className="brief-panel-overlay">
      {/* Loading Overlay */}
      {isLoading && (
        <div className="brief-loading-overlay">
          <div className="brief-loading-content">
            <img src="/ugc3-logo.png" alt="UGC3" className="brief-loading-logo" />
            <div className="brief-loading-spinner"></div>
            <h3>Generating Your Brief</h3>
            <p className="loading-message">{loadingSteps[loadingStep]}</p>
          </div>
        </div>
      )}
      
      <div ref={panelRef} className="brief-panel-fullscreen brief-panel-namespace">
        {/* Top Header/Fold */}
        <div className="brief-panel-header">
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        {/* Step 1: Creation Method Selection */}
        {step === 1 && (
          <div className="step-content creation-method-step">
            <div className="step-header">
              <h2>Choose Your Method</h2>
              <p>Select how you'd like to create your UGC creative brief</p>
            </div>
            
            <div className="brief-creation-method-cards">
              <button 
                className={`brief-creation-method-card ${mode === 'ai' ? 'selected' : ''}`}
                onClick={() => setMode('ai')}
              >
                <div className="brief-method-icon">
                  <div className="method-icon-stack">
                    <img src="/ugc3-logo.png" alt="UGC3" className="method-logo-small" />
                    <Bot size={32} className="method-ai-icon" />
                  </div>
                </div>
                <div className="method-content">
                  <h3>Brief Assistant</h3>
                  <p>Let our AI assistant help you generate a professional brief based on your inputs. Perfect for quick, high-quality briefs.</p>
                  <div className="ai-brief-badge">
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                      <path d="M8 0L10.5 5.5L16 8L10.5 10.5L8 16L5.5 10.5L0 8L5.5 5.5L8 0Z" fill="currentColor"/>
                    </svg>
                    Smart & Fast
                  </div>
                </div>
              </button>
              
              <button 
                className={`brief-creation-method-card ${mode === 'manual' ? 'selected' : ''}`}
                onClick={() => setMode('manual')}
              >
                <div className="brief-method-icon">
                  <div className="method-icon-stack">
                    <div className="method-logo-placeholder"></div>
                    <PenTool size={32} className="method-manual-icon" />
                  </div>
                </div>
                <div className="method-content">
                  <h3>Create Manually</h3>
                  <p>Write your brief from scratch with full control. Ideal for custom requirements and specific needs.</p>
                  <div className="manual-control-badge">
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                      <path d="M8 0L10.5 5.5L16 8L10.5 10.5L8 16L5.5 10.5L0 8L5.5 5.5L8 0Z" fill="currentColor"/>
                    </svg>
                    Custom & Precise
                  </div>
                </div>
              </button>
            </div>
            
            {mode && (
              <button 
                className="btn btn-primary continue-btn"
                onClick={() => setStep(2)}
              >
                Continue
              </button>
            )}
          </div>
        )}

        {/* Step 2: Campaign & Brief Setup */}
        {step === 2 && (
          <div className="step-content campaign-setup-step">
            <div className="step-header">
              <h2>Setup Details</h2>
              <p>Select your campaign and give your brief a name</p>
            </div>
            
            <div className="setup-form">
              <div className="form-group">
                <label htmlFor="campaign-select">Campaign</label>
                <select
                  id="campaign-select"
                  className="form-select"
                  value={selectedCampaign}
                  onChange={handleCampaignChange}
                >
                  <option value="">Select a campaign...</option>
                  {campaigns.map(campaign => (
                    <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="brief-name">Brief Name</label>
                <input
                  id="brief-name"
                  type="text"
                  className="form-input"
                  placeholder="Enter brief name..."
                  value={prompts.campaignName}
                  onChange={e => setPrompts(p => ({ ...p, campaignName: e.target.value }))}
                />
                <p className="field-note">Assigned creators will see this name.</p>
              </div>
            </div>
            
            <div className="step-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setStep(1)}
              >
                ← Back
              </button>
              {selectedCampaign && prompts.campaignName && (
                <button
                  className="btn btn-primary"
                  onClick={() => setStep(3)}
                >
                  Continue
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Content Creation */}
        {step === 3 && (
          <div className="step-content content-creation-step">
            <div ref={responseTopRef} />
            
            {mode === 'ai' ? (
              !aiPromptComplete ? (
                <div className="ai-prompts-container">
                  <div className="prompts-title">
                    <h2>Brief Details</h2>
                    <p>Fill in the details below to generate your perfect UGC brief</p>
                  </div>
                  
                  <form className="ai-prompts-form">
                    {promptFields.map((field, index) => (
                      <div className="prompt-field" key={field.name}>
                        <div className="field-header">
                          <label htmlFor={field.name} className="field-label">
                            {field.label}
                            {prompts[field.name] && (
                              <span className="field-complete">✓</span>
                            )}
                          </label>
                          {prompts[field.name] && (
                            <span className="field-count">{prompts[field.name].length} characters</span>
                          )}
                        </div>
                        <textarea
                          id={field.name}
                          name={field.name}
                          value={prompts[field.name]}
                          onChange={handlePromptChange}
                          className="field-textarea"
                          rows={4}
                          placeholder={field.placeholder}
                        />
                      </div>
                    ))}
                  </form>
                  
                  <div className="prompts-actions">
                    <button 
                      type="button" 
                      className="btn btn-primary brief-generate-btn" 
                      onClick={handleGenerateBrief} 
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="loading-spinner"></span>
                          Generating Brief...
                        </>
                      ) : (
                        'Generate Brief'
                      )}
                    </button>
                    
                    {lastAiResponse && (
                      <button
                        className="btn btn-secondary restore-btn"
                        onClick={handleRestorePreviousBrief}
                      >
                        Restore Previous Brief
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="ai-brief-preview">
                  {renderAIResponse()}
                  <div className="preview-actions">
                    <div className="action-buttons">
                      <button 
                        className="btn btn-secondary back-btn" 
                        onClick={handleBackToPrompts}
                      >
                        ← Back to Prompts
                      </button>
                      <button 
                        className="btn btn-primary save-btn" 
                        onClick={handleSaveBrief} 
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <span className="loading-spinner"></span>
                            Saving...
                          </>
                        ) : (
                          'Save Brief'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="manual-brief-container">
                {renderManualBriefSections()}
                
                <div className="preview-actions">
                  <div className="action-buttons">
                    <button 
                      className="btn btn-primary save-btn" 
                      onClick={handleSaveManualBrief} 
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <span className="loading-spinner"></span>
                          Saving...
                        </>
                      ) : (
                        'Save Manual Brief'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="step-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setStep(2)}
              >
                ← Back
              </button>
            </div>
          </div>
        )}

        {showSuccess && (
          <div className="save-success-toast">
            Brief auto-saved successfully!
          </div>
        )}

        {showAutoSaveToast && (
          <div className="auto-save-toast">
            <span className="auto-save-icon">💾</span>
            Auto-saved
          </div>
        )}
      </div>
    </div>
  );
} 